import { ArrowForwardRounded, AttachMoneyRounded, LocationOnOutlined } from "@mui/icons-material";
import { Button, Card, CardContent, Chip, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import type { PublicOffer } from "./types";
import { useLanguage } from "../../../../contexts/LanguageContext";
import translations from "./OfferCard.translations.json";

type SupportedLanguage = keyof typeof translations;

interface OfferCardProps {
  offer: PublicOffer;
}

export const OfferCard = ({ offer }: OfferCardProps) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;

  return (
  <Card
    elevation={0}
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "16px",
      bgcolor: "#e0f2fe",
      border: "1px solid #334155",
      p: 1,
      boxShadow: "0 4px 12px rgba(14, 116, 144, 0.08)",
      backgroundColor: "#101b32"
    }}
  >
    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Stack spacing={0.5} minWidth={0}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.25, color: "#0369a1" }} noWrap>
            {offer.title || content.untitled}
          </Typography>
          <Typography variant="body2" sx={{ color: "#94A3B8", mt: 0.5, fontWeight: 600 }} noWrap>
            {offer.company || content.company}
          </Typography>
        </Stack>
        <Chip label={offer.jobType || content.any} size="small" sx={{ border: "1px solid rgba(255, 255, 255, 0.12)", bgcolor: "#1e293b", color: "#e2e8f0", fontWeight: 700, borderRadius: "8px" }} />
      </Stack>

      <Typography variant="body2" sx={{ color: "#94A3B8", lineHeight: 1.6 }}>
        {offer.description || content.description}
      </Typography>

      {offer.tag?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
          {offer.tag?.map((item) => <Chip key={item?.tag} label={item?.tag} size="small" sx={{ border: "1px solid rgba(255, 255, 255, 0.12)", bgcolor: "#1e293b", color: "#e2e8f0", fontWeight: 700, borderRadius: "8px" }} />)}
        </Stack>
      )}

      <Divider sx={{ mt: "auto" }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ color: "#94A3B8", pt: 0.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {(offer.location || offer.workPlace) && <LocationOnOutlined sx={{ fontSize: 18 }} />}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#94A3B8" }}>
            {offer.location?.label}{offer.workPlace ? ` • ${offer.workPlace}` : ""}
          </Typography>
        </Stack>
        {offer.salary?.label && <Stack direction="row" spacing={0.75} alignItems="center"><AttachMoneyRounded sx={{ fontSize: 18 }} /><Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#94A3B8;" }}>{offer.salary?.label}</Typography></Stack>}
      </Stack>

      <Button component={Link} to={offer.cta?.url || "#"} variant="contained" endIcon={<ArrowForwardRounded />} sx={{ alignSelf: "flex-end", mt: "auto", bgcolor: "#007a5a", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3, py: 0.8, "&:hover": { bgcolor: "#006147" } }}>
        {offer.cta?.label || content.apply}
      </Button>
    </CardContent>
  </Card>
  );
};
